/**
 * UsersManagementPage Component
 *
 * Administration page for managing user accounts.
 * Provides:
 * - Paginated list of users with search (username, email, ID)
 * - Filtering by subscription type
 * - View user details (subscription status, usage statistics)
 * - Enable/disable user accounts with confirmation
 * - Reset password with confirmation
 *
 * @validates Requirements 12.1, 12.2, 12.3, 12.4, 12.5, 12.6
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { SubscriptionType } from '@spellion/shared';
import { PendingDeletionsSection } from './PendingDeletionsSection';

// ============================================================================
// Types
// ============================================================================

/** Admin user profile from API */
interface AdminUserProfile {
  id: string;
  email: string;
  role: string;
  subscriptionType: SubscriptionType;
  isActive: boolean;
  emailVerified?: boolean;
  isLocked?: boolean;
  lockTTL?: number | null;
  lockReason?: 'login' | '2fa' | null;
  failedLoginAttempts: number;
  lockedUntil: string | null;
  lastLogin: string | null;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  /** Payment type: 'subscription' for recurring, 'one-time' for single payment */
  paymentType?: 'subscription' | 'one-time' | null;
  /** Subscription end date */
  subscriptionEndDate?: string | null;
  /** Account deletion requested date */
  deletionRequestedAt?: string | null;
  /** Account deletion scheduled date */
  deletionScheduledAt?: string | null;
  subscription?: {
    status: string;
    startsAt: string | null;
    endsAt: string | null;
    renewalDate: string | null;
  };
  stats?: {
    totalSearches: number;
    lastSearchAt: string | null;
  };
}

/** API response for users list */
interface UsersListResponse {
  success: boolean;
  data: AdminUserProfile[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

/** API response for status update */
interface StatusUpdateResponse {
  success: boolean;
  data?: {
    userId: string;
    isActive: boolean;
  };
  message?: string;
  error?: {
    code: string;
    message: string;
  };
}

/** API response for password reset */
interface PasswordResetResponse {
  success: boolean;
  data?: {
    userId: string;
  };
  message?: string;
  error?: {
    code: string;
    message: string;
  };
}

/** API response for user deletion */
interface UserDeleteResponse {
  success: boolean;
  data?: {
    userId: string;
    email: string;
  };
  message?: string;
  error?: {
    code: string;
    message: string;
  };
}

/** Filter state */
interface FilterState {
  search: string;
  subscriptionType: string;
  isActive: string;
}

/** Confirmation modal state */
interface ConfirmationState {
  isOpen: boolean;
  type: 'enable' | 'disable' | 'reset-password' | 'unlock' | 'change-subscription' | 'delete' | 'resend-verification' | 'cancel-deletion' | null;
  user: AdminUserProfile | null;
  targetSubscription?: SubscriptionType;
}

/** Subscription history entry */
interface SubscriptionHistoryEntry {
  id: string;
  userId: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  paymentType: 'subscription' | 'one-time' | null;
  amountPaid: number;
  paypalSubscriptionId: string | null;
  paypalOrderId: string | null;
  status: 'active' | 'completed' | 'cancelled' | 'expired';
  endedReason: 'expiration' | 'cancelled_by_user' | 'cancelled_by_admin' | 'payment_failed' | null;
  createdAt: string;
}

/** Subscription stats */
interface SubscriptionStats {
  totalSubscriptions: number;
  totalSpent: number;
  firstSubscriptionDate: string | null;
  lastSubscriptionDate: string | null;
}

/** API response for subscription history */
interface SubscriptionHistoryResponse {
  success: boolean;
  data: SubscriptionHistoryEntry[];
  stats?: SubscriptionStats;
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

// ============================================================================
// Constants
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const ITEMS_PER_PAGE = 20;

// ============================================================================
// Component
// ============================================================================

export function UsersManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useStore((state) => state.token);
  const currentUser = useStore((state) => state.user);

  // State
  const [users, setUsers] = useState<AdminUserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: ITEMS_PER_PAGE,
    total: 0,
    hasMore: false,
  });

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    search: searchParams.get('search') || '',
    subscriptionType: searchParams.get('subscriptionType') || '',
    isActive: searchParams.get('isActive') || '',
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  // Confirmation modal state
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    type: null,
    user: null,
  });

  // Action feedback state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Selected user for details view
  const [selectedUser, setSelectedUser] = useState<AdminUserProfile | null>(null);

  // Subscription history state
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [subscriptionHistory, setSubscriptionHistory] = useState<SubscriptionHistoryEntry[]>([]);
  const [subscriptionStats, setSubscriptionStats] = useState<SubscriptionStats | null>(null);

  // Build headers with auth token
  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  // Fetch users
  const fetchUsers = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(ITEMS_PER_PAGE));

      if (debouncedSearch) {
        params.set('search', debouncedSearch);
      }
      if (filters.subscriptionType) {
        params.set('subscriptionType', filters.subscriptionType);
      }
      if (filters.isActive) {
        params.set('isActive', filters.isActive);
      }

      const response = await fetch(
        `${API_BASE_URL}/admin/users?${params.toString()}`,
        { method: 'GET', headers: getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des utilisateurs');
      }

      const data: UsersListResponse = await response.json();

      if (data.success) {
        setUsers(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, filters.subscriptionType, filters.isActive, getHeaders]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch users on filter change
  useEffect(() => {
    fetchUsers(1);
  }, [fetchUsers]);

  // Update URL params on filter change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.subscriptionType) params.set('subscriptionType', filters.subscriptionType);
    if (filters.isActive) params.set('isActive', filters.isActive);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Handle filter change
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchUsers(page);
  };

  // Clear action message after timeout
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  // Open confirmation modal
  const openConfirmation = (
    type: 'enable' | 'disable' | 'reset-password' | 'unlock' | 'change-subscription' | 'delete' | 'resend-verification' | 'cancel-deletion',
    user: AdminUserProfile,
    targetSubscription?: SubscriptionType
  ) => {
    setConfirmation({ isOpen: true, type, user, targetSubscription });
  };

  // Close confirmation modal
  const closeConfirmation = () => {
    setConfirmation({ isOpen: false, type: null, user: null, targetSubscription: undefined });
  };

  // Handle enable/disable user
  const handleToggleUserStatus = async () => {
    if (!confirmation.user || !confirmation.type) return;

    setActionLoading(true);
    const userId = confirmation.user.id;
    const isActive = confirmation.type === 'enable';

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/status`,
        {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify({ isActive }),
        }
      );

      const data: StatusUpdateResponse = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: data.message || `Le compte a été ${isActive ? 'activé' : 'désactivé'} avec succès`,
        });
        // Update user in list
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, isActive: data.data?.isActive ?? isActive } : u
          )
        );
        // Update selected user if viewing details
        if (selectedUser?.id === userId) {
          setSelectedUser((prev) =>
            prev ? { ...prev, isActive: data.data?.isActive ?? isActive } : null
          );
        }
      } else {
        setActionMessage({
          type: 'error',
          text: data.error?.message || 'Une erreur est survenue',
        });
      }
    } catch {
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la mise à jour',
      });
    } finally {
      setActionLoading(false);
      closeConfirmation();
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!confirmation.user) return;

    setActionLoading(true);
    const userId = confirmation.user.id;

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/reset-password`,
        {
          method: 'POST',
          headers: getHeaders(),
        }
      );

      const data: PasswordResetResponse = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: data.message || 'Un email de réinitialisation a été envoyé',
        });
      } else {
        setActionMessage({
          type: 'error',
          text: data.error?.message || 'Une erreur est survenue',
        });
      }
    } catch {
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la réinitialisation',
      });
    } finally {
      setActionLoading(false);
      closeConfirmation();
    }
  };

  // Handle unlock user account
  const handleUnlockUser = async () => {
    if (!confirmation.user) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${confirmation.user.id}/unlock`,
        { method: 'POST', headers: getHeaders() }
      );

      const data = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: data.data?.message || `Le compte de ${confirmation.user.email} a été déverrouillé`,
        });
        // Update user in list to remove lock status
        setUsers((prev) =>
          prev.map((u) =>
            u.id === confirmation.user?.id ? { ...u, isLocked: false, lockTTL: null } : u
          )
        );
        closeConfirmation();
      } else {
        setActionMessage({
          type: 'error',
          text: data.error?.message || 'Erreur lors du déverrouillage',
        });
        closeConfirmation();
      }
    } catch (err) {
      console.error('Erreur déverrouillage:', err);
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors du déverrouillage',
      });
      closeConfirmation();
    } finally {
      setActionLoading(false);
    }
  };

  // Handle subscription change
  const handleSubscriptionChange = async () => {
    if (!confirmation.user || !confirmation.targetSubscription) return;

    setActionLoading(true);
    const userId = confirmation.user.id;
    const newSubscription = confirmation.targetSubscription;

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/subscription`,
        {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify({ subscriptionType: newSubscription }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: data.data?.message || `Abonnement modifié en ${newSubscription === SubscriptionType.PREMIUM ? 'Premium' : 'Gratuit'}`,
        });
        // Update user in list
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, subscriptionType: newSubscription, role: data.data?.role || u.role } : u
          )
        );
        // Update selected user if viewing details
        if (selectedUser?.id === userId) {
          setSelectedUser((prev) =>
            prev ? { ...prev, subscriptionType: newSubscription, role: data.data?.role || prev.role } : null
          );
        }
      } else {
        setActionMessage({
          type: 'error',
          text: data.error?.message || 'Une erreur est survenue',
        });
      }
    } catch {
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la modification de l\'abonnement',
      });
    } finally {
      setActionLoading(false);
      closeConfirmation();
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!confirmation.user) return;

    setActionLoading(true);
    const userId = confirmation.user.id;
    const userEmail = confirmation.user.email;

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}`,
        {
          method: 'DELETE',
          headers: getHeaders(),
        }
      );

      const data: UserDeleteResponse = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: data.message || `Le compte ${userEmail} a été supprimé`,
        });
        // Remove user from list
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        // Update pagination total
        setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
        // Close details modal if viewing this user
        if (selectedUser?.id === userId) {
          setSelectedUser(null);
        }
      } else {
        setActionMessage({
          type: 'error',
          text: data.error?.message || 'Une erreur est survenue',
        });
      }
    } catch {
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la suppression',
      });
    } finally {
      setActionLoading(false);
      closeConfirmation();
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!confirmation.user) return;

    setActionLoading(true);
    const userId = confirmation.user.id;
    const userEmail = confirmation.user.email;

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/resend-verification`,
        {
          method: 'POST',
          headers: getHeaders(),
        }
      );

      const data = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: data.data?.message || `Email de vérification renvoyé à ${userEmail}`,
        });
      } else {
        setActionMessage({
          type: 'error',
          text: data.error?.message || 'Une erreur est survenue',
        });
      }
    } catch {
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de l\'envoi',
      });
    } finally {
      setActionLoading(false);
      closeConfirmation();
    }
  };

  // Handle cancel deletion (reactivate account pending deletion)
  const handleCancelDeletion = async () => {
    if (!confirmation.user) return;

    setActionLoading(true);
    const userId = confirmation.user.id;

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/reactivate`,
        {
          method: 'POST',
          headers: getHeaders(),
        }
      );

      const data = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: 'La suppression du compte a été annulée. Le compte est réactivé.',
        });
        // Update user in list - clear deletion dates and reactivate
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId 
              ? { ...u, deletionRequestedAt: null, deletionScheduledAt: null, isActive: true } 
              : u
          )
        );
        if (selectedUser?.id === userId) {
          setSelectedUser((prev) =>
            prev ? { ...prev, deletionRequestedAt: null, deletionScheduledAt: null, isActive: true } : null
          );
        }
      } else {
        setActionMessage({
          type: 'error',
          text: data.error?.message || 'Une erreur est survenue',
        });
      }
    } catch {
      setActionMessage({
        type: 'error',
        text: 'Une erreur est survenue lors de la réactivation',
      });
    } finally {
      setActionLoading(false);
      closeConfirmation();
    }
  };

  // Handle confirmation action
  const handleConfirmAction = () => {
    if (confirmation.type === 'enable' || confirmation.type === 'disable') {
      handleToggleUserStatus();
    } else if (confirmation.type === 'reset-password') {
      handlePasswordReset();
    } else if (confirmation.type === 'unlock') {
      handleUnlockUser();
    } else if (confirmation.type === 'change-subscription') {
      handleSubscriptionChange();
    } else if (confirmation.type === 'delete') {
      handleDeleteUser();
    } else if (confirmation.type === 'resend-verification') {
      handleResendVerification();
    } else if (confirmation.type === 'cancel-deletion') {
      handleCancelDeletion();
    }
  };

  // Navigate to user detail page
  const handleViewDetails = (user: AdminUserProfile) => {
    navigate(`/admin/users/${user.id}`);
  };

  // Fetch subscription history for selected user
  const fetchSubscriptionHistory = async (userId: string) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/subscription-history?limit=50`,
        { method: 'GET', headers: getHeaders() }
      );

      if (response.ok) {
        const data: SubscriptionHistoryResponse = await response.json();
        if (data.success) {
          setSubscriptionHistory(data.data);
          if (data.stats) {
            setSubscriptionStats(data.stats);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch subscription history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Toggle history section
  const handleToggleHistory = () => {
    if (!showHistory && selectedUser) {
      fetchSubscriptionHistory(selectedUser.id);
    }
    setShowHistory(!showHistory);
  };

  // Get history status badge style and label
  const getHistoryStatusBadge = (status: string, endedReason: string | null) => {
    switch (status) {
      case 'active':
        return { style: 'bg-success-100 text-success-700', label: '✓ Actif' };
      case 'expired':
        return { style: 'bg-slate-100 text-slate-600', label: '⏱ Expiré' };
      case 'cancelled':
        const reason = endedReason === 'cancelled_by_admin' ? ' (admin)' : ' (utilisateur)';
        return { style: 'bg-orange-100 text-orange-700', label: `✕ Annulé${reason}` };
      case 'completed':
        return { style: 'bg-blue-100 text-blue-700', label: '✓ Terminé' };
      default:
        return { style: 'bg-slate-100 text-slate-500', label: status };
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get subscription badge style
  const getSubscriptionBadge = (type: SubscriptionType, role?: string) => {
    if (role === 'admin') {
      return 'bg-rose-100 text-rose-700';
    }
    switch (type) {
      case SubscriptionType.PREMIUM:
        return 'bg-primary-100 text-primary-700';
      case SubscriptionType.FREE:
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  // Get subscription label
  const getSubscriptionLabel = (type: SubscriptionType, role?: string) => {
    if (role === 'admin') {
      return 'Administrateur';
    }
    switch (type) {
      case SubscriptionType.PREMIUM:
        return 'Premium';
      case SubscriptionType.FREE:
      default:
        return 'Gratuit';
    }
  };

  // Get payment type badge style and label
  const getPaymentTypeBadge = (paymentType: 'subscription' | 'one-time' | null | undefined) => {
    switch (paymentType) {
      case 'subscription':
        return { style: 'bg-blue-100 text-blue-700', label: '🔄 Annuel' };
      case 'one-time':
        return { style: 'bg-amber-100 text-amber-700', label: '💳 Mensuel' };
      default:
        return { style: 'bg-slate-100 text-slate-500', label: '—' };
    }
  };

  // Get status badge style and label
  const getStatusBadge = (user: AdminUserProfile): { style: string; label: string } => {
    // En cours de suppression - priorité sur désactivé
    if (user.deletionRequestedAt) {
      return { 
        style: 'bg-orange-100 text-orange-700', 
        label: 'En cours de suppression' 
      };
    }
    // Désactivé par admin
    if (!user.isActive) {
      return { style: 'bg-red-100 text-red-700', label: 'Désactivé' };
    }
    // Actif
    return { style: 'bg-success-100 text-success-700', label: 'Actif' };
  };

  // Calculate total pages
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">
            Gestion des utilisateurs
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            {pagination.total} utilisateur{pagination.total > 1 ? 's' : ''} au total
          </p>
        </div>
      </div>

      {/* Pending Deletions Section */}
      <PendingDeletionsSection />

      {/* Action Message */}
      {actionMessage && (
        <div
          className={`flex items-center gap-3 p-4 rounded-xl border ${
            actionMessage.type === 'success'
              ? 'bg-success-50 border-success-200 text-success-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
          role="alert"
        >
          {actionMessage.type === 'success' ? (
            <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
          ) : (
            <ExclamationIcon className="w-5 h-5 flex-shrink-0" />
          )}
          <p className="flex-1">{actionMessage.text}</p>
          <button
            type="button"
            onClick={() => setActionMessage(null)}
            className="p-1 hover:bg-black/10 rounded transition-colors"
            aria-label="Fermer"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4 max-w-full box-border">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          {/* Search */}
          <div className="flex-1 min-w-0 md:max-w-md">
            <label htmlFor="search" className="sr-only">
              Rechercher
            </label>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                id="search"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  text-sm placeholder:text-slate-400 box-border"
              />
            </div>
          </div>

          {/* Subscription Filter */}
          <div className="min-w-0 md:w-40">
            <label htmlFor="subscriptionType" className="sr-only">
              Type d'abonnement
            </label>
            <select
              id="subscriptionType"
              value={filters.subscriptionType}
              onChange={(e) => handleFilterChange('subscriptionType', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                text-sm bg-white box-border"
            >
              <option value="">Abonnement</option>
              <option value="free">Gratuit</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="min-w-0 md:w-40">
            <label htmlFor="isActive" className="sr-only">
              Statut
            </label>
            <select
              id="isActive"
              value={filters.isActive}
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                text-sm bg-white box-border"
            >
              <option value="">Statut</option>
              <option value="true">Actifs</option>
              <option value="false">Désactivés</option>
              <option value="pending-deletion">Suppression</option>
              <option value="unverified">Non vérifiés</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => fetchUsers(pagination.page)}
            className="mt-2 text-sm underline hover:no-underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Users Table/Cards */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <LoadingSpinner />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center p-12 text-slate-500">
            <UsersIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Aucun utilisateur trouvé</p>
          </div>
        ) : (
          <>
            {/* Desktop Table - hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Abonnement
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-28">
                      Paiement
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-40">
                      Statut
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Dernière connexion
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium flex-shrink-0">
                            {user.email.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900" title={user.email}>
                              {user.email}
                            </p>
                            <p className="text-xs text-slate-500 font-mono" title={user.id}>
                              {user.id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSubscriptionBadge(
                            user.subscriptionType, user.role
                          )}`}
                        >
                          {getSubscriptionLabel(user.subscriptionType, user.role)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {user.subscriptionType === SubscriptionType.PREMIUM ? (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentTypeBadge(user.paymentType).style}`}
                          >
                            {getPaymentTypeBadge(user.paymentType).label}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full text-center ${getStatusBadge(user).style}`}
                          >
                            {getStatusBadge(user).label}
                          </span>
                          {user.emailVerified === false && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              ⚠️ Non vérifié
                            </span>
                          )}
                          {user.isLocked && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                              🔒 Verrouillé
                              {user.lockReason === '2fa' ? ' (2FA)' : ' (login)'}
                              {user.lockTTL && user.lockTTL > 0 && (
                                <span className="ml-1">
                                  - {Math.ceil(user.lockTTL / 60)} min
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-500 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span>{formatDate(user.lastLogin)}</span>
                          {user.deletionScheduledAt && (
                            <span className="text-xs text-orange-600 font-medium">
                              🗑️ Supprimer le {new Date(user.deletionScheduledAt).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewDetails(user)}
                            className="p-2 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Voir les détails"
                            aria-label={`Voir les détails de ${user.email}`}
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          {/* Hide disable button for current user (admin can't disable themselves) */}
                          {currentUser?.id !== user.id && (
                            <button
                              type="button"
                              onClick={() =>
                                openConfirmation(
                                  user.isActive ? 'disable' : 'enable',
                                  user
                                )
                              }
                              className={`p-2 rounded-lg transition-colors ${
                                user.isActive
                                  ? 'text-slate-400 hover:text-red-600 hover:bg-red-50'
                                  : 'text-slate-400 hover:text-success-600 hover:bg-success-50'
                              }`}
                              title={user.isActive ? 'Désactiver' : 'Activer'}
                              aria-label={
                                user.isActive
                                  ? `Désactiver ${user.email}`
                                  : `Activer ${user.email}`
                              }
                            >
                              {user.isActive ? (
                                <BanIcon className="w-5 h-5" />
                              ) : (
                                <CheckIcon className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          {/* Cancel deletion button - only for accounts pending deletion */}
                          {user.deletionRequestedAt && currentUser?.id !== user.id && (
                            <button
                              type="button"
                              onClick={() => openConfirmation('cancel-deletion', user)}
                              className="p-2 text-orange-500 hover:text-success-600 hover:bg-success-50 rounded-lg transition-colors"
                              title="Annuler la suppression"
                              aria-label={`Annuler la suppression du compte de ${user.email}`}
                            >
                              <RestoreIcon className="w-5 h-5" />
                            </button>
                          )}
                          {user.isLocked && (
                            <button
                              type="button"
                              onClick={() => openConfirmation('unlock', user)}
                              className="p-2 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Déverrouiller le compte"
                              aria-label={`Déverrouiller le compte de ${user.email}`}
                            >
                              <UnlockIcon className="w-5 h-5" />
                            </button>
                          )}
                          {/* Hide reset password button for current user */}
                          {currentUser?.id !== user.id && (
                            <button
                              type="button"
                              onClick={() => openConfirmation('reset-password', user)}
                              className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Réinitialiser le mot de passe"
                              aria-label={`Réinitialiser le mot de passe de ${user.email}`}
                            >
                              <KeyIcon className="w-5 h-5" />
                            </button>
                          )}
                          {/* Resend verification email - only for unverified accounts */}
                          {user.emailVerified === false && (
                            <button
                              type="button"
                              onClick={() => openConfirmation('resend-verification', user)}
                              className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Renvoyer l'email de vérification"
                              aria-label={`Renvoyer l'email de vérification à ${user.email}`}
                            >
                              <MailIcon className="w-5 h-5" />
                            </button>
                          )}
                          {/* Delete button - only for non-admin users and not current user */}
                          {currentUser?.id !== user.id && user.role !== 'admin' && (
                            <button
                              type="button"
                              onClick={() => openConfirmation('delete', user)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer le compte"
                              aria-label={`Supprimer le compte de ${user.email}`}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards - shown only on mobile */}
            <div className="md:hidden divide-y divide-slate-100">
              {users.map((user) => (
                <div key={user.id} className="p-4 space-y-3 overflow-hidden">
                  {/* User header */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium flex-shrink-0">
                      {user.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {user.email}
                      </p>
                      <p className="text-xs text-slate-500 font-mono truncate" title={user.id}>
                        {user.id.slice(0, 20)}...
                      </p>
                    </div>
                  </div>

                  {/* Badges row - abonnement et statut */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSubscriptionBadge(
                        user.subscriptionType, user.role
                      )}`}
                    >
                      {getSubscriptionLabel(user.subscriptionType, user.role)}
                    </span>
                    {user.subscriptionType === SubscriptionType.PREMIUM && (
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentTypeBadge(user.paymentType).style}`}
                      >
                        {getPaymentTypeBadge(user.paymentType).label}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full text-center ${getStatusBadge(user).style}`}
                    >
                      {getStatusBadge(user).label}
                    </span>
                    {user.emailVerified === false && (
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                        ⚠️ Non vérifié
                      </span>
                    )}
                    {user.isLocked && (
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                        🔒 Verrouillé
                      </span>
                    )}
                  </div>

                  {/* Date info - separate row - 8px font size */}
                  <div className="text-slate-400">
                    <span style={{ fontSize: '10px', display: 'block' }}>Dernière connexion : {formatDate(user.lastLogin)}</span>
                    {user.deletionScheduledAt && (
                      <span className="block text-orange-600 font-medium" style={{ fontSize: '10px' }}>
                        🗑️ Supprimer le {new Date(user.deletionScheduledAt).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>

                  {/* Actions - icons only on mobile */}
                  <div className="flex items-center gap-1 pt-2 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => handleViewDetails(user)}
                      className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Voir les détails"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    {/* Hide disable button for current user (admin can't disable themselves) */}
                    {currentUser?.id !== user.id && (
                      <button
                        type="button"
                        onClick={() =>
                          openConfirmation(
                            user.isActive ? 'disable' : 'enable',
                            user
                          )
                        }
                        className={`p-1.5 rounded-lg transition-colors ${
                          user.isActive
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-success-600 hover:bg-success-50'
                        }`}
                        title={user.isActive ? 'Désactiver' : 'Activer'}
                      >
                        {user.isActive ? (
                          <BanIcon className="w-4 h-4" />
                        ) : (
                          <CheckIcon className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {/* Cancel deletion button */}
                    {user.deletionRequestedAt && currentUser?.id !== user.id && (
                      <button
                        type="button"
                        onClick={() => openConfirmation('cancel-deletion', user)}
                        className="p-1.5 text-success-600 hover:bg-success-50 rounded-lg transition-colors"
                        title="Annuler la suppression"
                      >
                        <RestoreIcon className="w-4 h-4" />
                      </button>
                    )}
                    {user.isLocked && (
                      <button
                        type="button"
                        onClick={() => openConfirmation('unlock', user)}
                        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Déverrouiller"
                      >
                        <UnlockIcon className="w-4 h-4" />
                      </button>
                    )}
                    {currentUser?.id !== user.id && (
                      <button
                        type="button"
                        onClick={() => openConfirmation('reset-password', user)}
                        className="p-1.5 text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
                        title="Réinitialiser le mot de passe"
                      >
                        <KeyIcon className="w-4 h-4" />
                      </button>
                    )}
                    {user.emailVerified === false && (
                      <button
                        type="button"
                        onClick={() => openConfirmation('resend-verification', user)}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Renvoyer l'email de vérification"
                      >
                        <MailIcon className="w-4 h-4" />
                      </button>
                    )}
                    {currentUser?.id !== user.id && user.role !== 'admin' && (
                      <button
                        type="button"
                        onClick={() => openConfirmation('delete', user)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer le compte"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {!isLoading && users.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Page {pagination.page} sur {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                  hover:bg-slate-50 transition-colors"
              >
                Précédent
              </button>
              <button
                type="button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasMore}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                  hover:bg-slate-50 transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-details-title"
        >
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setSelectedUser(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h2 id="user-details-title" className="text-lg font-semibold text-slate-900">
                Détails de l'utilisateur
              </h2>
              <button
                type="button"
                onClick={() => setSelectedUser(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-2xl font-medium text-slate-600">
                  {selectedUser.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-lg font-medium text-slate-900">
                    {selectedUser.email}
                  </p>
                  <p className="text-sm text-slate-500 font-mono">
                    ID: {selectedUser.id}
                  </p>
                </div>
              </div>

              {/* Status Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                    Abonnement
                  </p>
                  <span
                    className={`inline-flex px-2 py-1 text-sm font-medium rounded-full ${getSubscriptionBadge(
                      selectedUser.subscriptionType, selectedUser.role
                    )}`}
                  >
                    {getSubscriptionLabel(selectedUser.subscriptionType, selectedUser.role)}
                  </span>
                  {selectedUser.subscriptionType === SubscriptionType.PREMIUM && selectedUser.role !== 'admin' && (
                    <div className="mt-2">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentTypeBadge(selectedUser.paymentType).style}`}
                      >
                        {getPaymentTypeBadge(selectedUser.paymentType).label}
                      </span>
                    </div>
                  )}
                  {/* Subscription change button - only for non-admin users */}
                  {selectedUser.role !== 'admin' && (
                    <button
                      type="button"
                      onClick={() => {
                        const targetType = selectedUser.subscriptionType === SubscriptionType.PREMIUM
                          ? SubscriptionType.FREE
                          : SubscriptionType.PREMIUM;
                        setSelectedUser(null);
                        openConfirmation('change-subscription', selectedUser, targetType);
                      }}
                      className="mt-2 w-full px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors flex items-center justify-center gap-1"
                    >
                      <SwitchIcon className="w-3.5 h-3.5" />
                      {selectedUser.subscriptionType === SubscriptionType.PREMIUM
                        ? 'Passer en Gratuit'
                        : 'Passer en Premium'}
                    </button>
                  )}
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                    Statut
                  </p>
                  <span
                    className={`inline-flex items-center justify-center px-2 py-1 text-sm font-medium rounded-full text-center ${getStatusBadge(selectedUser).style}`}
                  >
                    {getStatusBadge(selectedUser).label}
                  </span>
                </div>
              </div>

              {/* Details List */}
              <dl className="space-y-3">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <dt className="text-sm text-slate-500">Rôle</dt>
                  <dd className="text-sm font-medium text-slate-900 capitalize">
                    {selectedUser.role}
                  </dd>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <dt className="text-sm text-slate-500">Dernière connexion</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {formatDate(selectedUser.lastLogin)}
                  </dd>
                </div>
                {selectedUser.deletionScheduledAt && (
                  <div className="flex justify-between py-2 border-b border-slate-100 bg-orange-50 -mx-4 px-4">
                    <dt className="text-sm text-orange-700 font-medium">🗑️ Suppression prévue</dt>
                    <dd className="text-sm font-bold text-orange-700">
                      {new Date(selectedUser.deletionScheduledAt).toLocaleDateString('fr-FR')}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <dt className="text-sm text-slate-500">Inscrit le</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {formatDate(selectedUser.createdAt)}
                  </dd>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <dt className="text-sm text-slate-500">Fuseau horaire</dt>
                  <dd className="text-sm font-medium text-slate-900">
                    {selectedUser.timezone || 'UTC'}
                  </dd>
                </div>

                {selectedUser.stats && (
                  <>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <dt className="text-sm text-slate-500">Total recherches</dt>
                      <dd className="text-sm font-medium text-slate-900">
                        {selectedUser.stats.totalSearches}
                      </dd>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <dt className="text-sm text-slate-500">Dernière recherche</dt>
                      <dd className="text-sm font-medium text-slate-900">
                        {formatDate(selectedUser.stats.lastSearchAt)}
                      </dd>
                    </div>
                  </>
                )}
                {selectedUser.subscriptionEndDate && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <dt className="text-sm text-slate-500">Expiration abonnement</dt>
                    <dd className="text-sm font-medium text-slate-900">
                      {formatDate(selectedUser.subscriptionEndDate)}
                    </dd>
                  </div>
                )}
                {selectedUser.subscription && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <dt className="text-sm text-slate-500">Statut abonnement</dt>
                    <dd className="text-sm font-medium text-slate-900 capitalize">
                      {selectedUser.subscription.status}
                    </dd>
                  </div>
                )}
                {selectedUser.failedLoginAttempts > 0 && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <dt className="text-sm text-slate-500">Échecs de connexion</dt>
                    <dd className="text-sm font-medium text-red-600">
                      {selectedUser.failedLoginAttempts}
                    </dd>
                  </div>
                )}
              </dl>

              {/* Subscription History Section */}
              <div className="border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={handleToggleHistory}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <HistoryIcon className="w-5 h-5 text-primary-600" />
                    <span className="font-medium text-slate-900">Historique des achats</span>
                    {subscriptionStats && subscriptionStats.totalSubscriptions > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                        {subscriptionStats.totalSubscriptions}
                      </span>
                    )}
                  </div>
                  <ChevronIcon className={`w-5 h-5 text-slate-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                </button>

                {showHistory && (
                  <div className="mt-3 space-y-3">
                    {/* Stats summary */}
                    {subscriptionStats && subscriptionStats.totalSubscriptions > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-primary-50 rounded-lg p-3">
                          <p className="text-xs text-primary-600 uppercase tracking-wider">Total dépensé</p>
                          <p className="text-lg font-bold text-primary-700">{subscriptionStats.totalSpent.toFixed(2)} €</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Abonnements</p>
                          <p className="text-lg font-bold text-slate-700">{subscriptionStats.totalSubscriptions}</p>
                        </div>
                      </div>
                    )}

                    {/* History list */}
                    {historyLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner className="w-6 h-6 text-primary-600" />
                      </div>
                    ) : subscriptionHistory.length === 0 ? (
                      <div className="text-center py-6 text-slate-500">
                        <p>Aucun historique d'achat</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {subscriptionHistory.map((entry) => (
                          <div
                            key={entry.id}
                            className="bg-white border border-slate-200 rounded-lg p-3 space-y-2"
                          >
                            <div className="flex items-center justify-between">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getHistoryStatusBadge(entry.status, entry.endedReason).style}`}>
                                {getHistoryStatusBadge(entry.status, entry.endedReason).label}
                              </span>
                              <span className="text-sm font-semibold text-slate-900">
                                {entry.amountPaid.toFixed(2)} €
                              </span>
                            </div>
                            <div className="text-xs text-slate-500 space-y-1">
                              <div className="flex justify-between">
                                <span>Début :</span>
                                <span className="font-medium text-slate-700">
                                  {new Date(entry.subscriptionStartDate).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Fin :</span>
                                <span className="font-medium text-slate-700">
                                  {new Date(entry.subscriptionEndDate).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Type :</span>
                                <span className="font-medium text-slate-700">
                                  {entry.paymentType === 'one-time' ? 'Mensuel' : entry.paymentType === 'subscription' ? 'Annuel' : '—'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUser(null);
                    openConfirmation(
                      selectedUser.isActive ? 'disable' : 'enable',
                      selectedUser
                    );
                  }}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    selectedUser.isActive
                      ? 'bg-red-50 text-red-700 hover:bg-red-100'
                      : 'bg-success-50 text-success-700 hover:bg-success-100'
                  }`}
                >
                  {selectedUser.isActive ? 'Désactiver le compte' : 'Activer le compte'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUser(null);
                    openConfirmation('reset-password', selectedUser);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
                >
                  Réinitialiser le mot de passe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmation.isOpen && confirmation.user && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirmation-title"
        >
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={closeConfirmation}
          />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  confirmation.type === 'enable'
                    ? 'bg-success-100'
                    : confirmation.type === 'disable'
                    ? 'bg-red-100'
                    : confirmation.type === 'unlock'
                    ? 'bg-blue-100'
                    : confirmation.type === 'change-subscription'
                    ? 'bg-primary-100'
                    : confirmation.type === 'delete'
                    ? 'bg-red-100'
                    : confirmation.type === 'resend-verification'
                    ? 'bg-amber-100'
                    : confirmation.type === 'cancel-deletion'
                    ? 'bg-success-100'
                    : 'bg-orange-100'
                }`}
              >
                {confirmation.type === 'enable' ? (
                  <CheckIcon className="w-8 h-8 text-success-600" />
                ) : confirmation.type === 'disable' ? (
                  <BanIcon className="w-8 h-8 text-red-600" />
                ) : confirmation.type === 'unlock' ? (
                  <UnlockIcon className="w-8 h-8 text-blue-600" />
                ) : confirmation.type === 'change-subscription' ? (
                  <SwitchIcon className="w-8 h-8 text-primary-600" />
                ) : confirmation.type === 'delete' ? (
                  <TrashIcon className="w-8 h-8 text-red-600" />
                ) : confirmation.type === 'resend-verification' ? (
                  <MailIcon className="w-8 h-8 text-amber-600" />
                ) : confirmation.type === 'cancel-deletion' ? (
                  <RestoreIcon className="w-8 h-8 text-success-600" />
                ) : (
                  <KeyIcon className="w-8 h-8 text-orange-600" />
                )}
              </div>

              <h2
                id="confirmation-title"
                className="text-xl font-bold text-slate-900 mb-2"
              >
                {confirmation.type === 'enable'
                  ? 'Activer le compte'
                  : confirmation.type === 'disable'
                  ? 'Désactiver le compte'
                  : confirmation.type === 'unlock'
                  ? 'Déverrouiller le compte'
                  : confirmation.type === 'change-subscription'
                  ? 'Changer l\'abonnement'
                  : confirmation.type === 'delete'
                  ? 'Supprimer le compte'
                  : confirmation.type === 'resend-verification'
                  ? 'Renvoyer l\'email de vérification'
                  : confirmation.type === 'cancel-deletion'
                  ? 'Annuler la suppression'
                  : 'Réinitialiser le mot de passe'}
              </h2>

              <p className="text-slate-600 mb-6">
                {confirmation.type === 'enable'
                  ? `Voulez-vous activer le compte de ${confirmation.user.email} ?`
                  : confirmation.type === 'disable'
                  ? `Voulez-vous désactiver le compte de ${confirmation.user.email} ? L'utilisateur ne pourra plus se connecter.`
                  : confirmation.type === 'unlock'
                  ? `Voulez-vous déverrouiller le compte de ${confirmation.user.email} ? L'utilisateur pourra à nouveau se connecter immédiatement.`
                  : confirmation.type === 'change-subscription'
                  ? `Voulez-vous passer ${confirmation.user.email} en ${confirmation.targetSubscription === SubscriptionType.PREMIUM ? 'Premium' : 'Gratuit'} ?`
                  : confirmation.type === 'delete'
                  ? `Voulez-vous définitivement supprimer le compte de ${confirmation.user.email} ? Cette action est irréversible et supprimera toutes les données associées.`
                  : confirmation.type === 'resend-verification'
                  ? `Voulez-vous renvoyer un email de vérification à ${confirmation.user.email} ?`
                  : confirmation.type === 'cancel-deletion'
                  ? `Voulez-vous annuler la suppression du compte de ${confirmation.user.email} ? Le compte sera réactivé et l'utilisateur pourra se reconnecter.`
                  : `Voulez-vous envoyer un email de réinitialisation de mot de passe à ${confirmation.user.email} ?`}
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeConfirmation}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg
                    bg-slate-100 text-slate-700 hover:bg-slate-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAction}
                  disabled={actionLoading}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg text-white
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors ${
                      confirmation.type === 'enable'
                        ? 'bg-success-600 hover:bg-success-700'
                        : confirmation.type === 'disable'
                        ? 'bg-red-600 hover:bg-red-700'
                        : confirmation.type === 'unlock'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : confirmation.type === 'change-subscription'
                        ? 'bg-primary-600 hover:bg-primary-700'
                        : confirmation.type === 'delete'
                        ? 'bg-red-600 hover:bg-red-700'
                        : confirmation.type === 'resend-verification'
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : confirmation.type === 'cancel-deletion'
                        ? 'bg-success-600 hover:bg-success-700'
                        : 'bg-orange-600 hover:bg-orange-700'
                    }`}
                >
                  {actionLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner className="w-4 h-4" />
                      <span>Chargement...</span>
                    </span>
                  ) : (
                    'Confirmer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function BanIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
    </svg>
  );
}

function UnlockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function RestoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ExclamationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function SwitchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  );
}

function LoadingSpinner({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function HistoryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

export default UsersManagementPage;
